import React, { useState, useEffect } from 'react';
import { Card, Btn, Modal, Input, Select, FormGrid, SectionHeader, Badge, Table, RNLLoader } from '../components/UI';
import { fmt, fmtDate } from '../utils';
import * as api from '../api';
import { useNavigate, useSearchParams } from 'react-router-dom';

const STATUS_OPTS = ['planning', 'active', 'on_hold', 'completed', 'cancelled'].map(s => ({ value: s, label: s }));
const CONTRACT_OPTS = ['', ...['lump_sum', 'remeasured', 'cost_plus', 'unit_rate']].map(s => ({ value: s, label: s || '— Select Type —' }));

function AdminProjectDashboardView({ project, toast }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category');
  const setActiveCategory = (cat) => {
    if (cat) setSearchParams({ tab: 'projects', id: project.project_id, category: cat });
    else setSearchParams({ tab: 'projects', id: project.project_id });
  };
  return (
    <div key={project.project_id} className="fade-in" style={{ padding: '0 8px' }}>
      {/* UPPER SECTION */}
      <div style={{ background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)', padding: '24px 32px', borderRadius: '16px 16px 0 0', border: '1px solid #cbd5e1', borderBottom: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: 0, color: '#0f172a' }}>{project.project_name}</h2>
          <span style={{ background: '#0f172a', color: '#fff', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>
            ADMIN CONFIGURATION
          </span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, margin: '6px 0 0 0' }}>
          Code: <strong style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{project.project_code}</strong>
          {project.client_name && ` · Client: ${project.client_name}`}
          {(project.start_date || project.end_date) && ` · Timeline: ${project.start_date ? fmtDate(project.start_date) : 'TBA'} — ${project.end_date ? fmtDate(project.end_date) : 'TBA'}`}
        </p>
      </div>

      {/* CONTENT AREA */}
      <Card style={{ padding: 32, borderRadius: '0 0 16px 16px', borderTop: '1px solid #cbd5e1', borderTopLeftRadius: 0, borderTopRightRadius: 0, minHeight: 300 }}>
        {activeCategory === null && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 24 }}>
            <Card className="toggle-on-hover black-border-card" onClick={() => navigate(`/admin/projects/${project.project_id}/project-divisions`)} style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16, background: 'var(--bg-elevated)', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <span style={{ fontSize: 36, padding: 16, borderRadius: 16, background: '#e0e7ff' }}>🏢</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Project Divisions</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Blocks, Zones & Structures</div>
              </div>
            </Card>

            <Card className="toggle-on-hover black-border-card" onClick={() => navigate(`/admin/projects/${project.project_id}/boq/boqbudgets`)} style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16, background: 'var(--bg-elevated)', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <span style={{ fontSize: 36, padding: 16, borderRadius: 16, background: '#e0f2fe' }}>💹</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Budget Management</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Manage project budgets</div>
              </div>
            </Card>

            <Card className="toggle-on-hover black-border-card" onClick={() => navigate(`/admin/projects/${project.project_id}/boq`)} style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16, background: 'var(--bg-elevated)', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <span style={{ fontSize: 36, padding: 16, borderRadius: 16, background: '#ede9fe' }}>📊</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>BOQ Management</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Manage Bill of Quantities</div>
              </div>
            </Card>
            <Card className="toggle-on-hover black-border-card" onClick={() => setActiveCategory('DMR')} style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16, background: 'var(--bg-elevated)', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <span style={{ fontSize: 36, padding: 16, borderRadius: 16, background: '#ffedd5' }}>🧱</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>DMR Management</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Sub-sections & Entries</div>
              </div>
            </Card>

            <Card className="toggle-on-hover black-border-card" onClick={() => setActiveCategory('LABOUR')} style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16, background: 'var(--bg-elevated)', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <span style={{ fontSize: 36, padding: 16, borderRadius: 16, background: '#ecfdf5' }}>👷‍♂️</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Labour Management</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Entries & Budgets</div>
              </div>
            </Card>
          </div>
        )}

        {activeCategory === 'DMR' && (
          <div className="fade-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>DMR Management</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 24 }}>
              <Card className="toggle-on-hover black-border-card" onClick={() => navigate(`/projects/${project.project_id}/direct-material/subsections?admin=true`)} style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, background: '#fef3c7', cursor: 'pointer' }}>
                <span style={{ fontSize: 32, padding: 12, borderRadius: 12, background: '#fff' }}>📂</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Sub-sections</span>
              </Card>
              <Card className="toggle-on-hover black-border-card" onClick={() => navigate(`/projects/${project.project_id}/direct-material/entries?admin=true`)} style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, background: '#ffedd5', cursor: 'pointer' }}>
                <span style={{ fontSize: 32, padding: 12, borderRadius: 12, background: '#fff' }}>🧱</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>DMR Entries</span>
              </Card>
            </div>
          </div>
        )}

        {activeCategory === 'LABOUR' && (
          <div className="fade-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Labour Cost Management</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 24 }}>
              <Card className="toggle-on-hover black-border-card" onClick={() => navigate(`/projects/${project.project_id}/labour/entries?admin=true`)} style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, background: '#ecfdf5', cursor: 'pointer' }}>
                <span style={{ fontSize: 32, padding: 12, borderRadius: 12, background: '#fff' }}>👷‍♂️</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Labour Entries</span>
              </Card>
              <Card className="toggle-on-hover black-border-card" onClick={() => navigate(`/projects/${project.project_id}/labour/budgets?admin=true`)} style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, background: '#dcfce7', cursor: 'pointer' }}>
                <span style={{ fontSize: 32, padding: 12, borderRadius: 12, background: '#fff' }}>💸</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Labour Budget</span>
              </Card>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export default function AdminProjectsTab({ toast, user, access = {} }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [viewProject, setViewProject] = useState(null);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [openingProject, setOpeningProject] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const [searchParams, setSearchParams] = useSearchParams();
  const activeProjectId = searchParams.get('id');

  const filteredProjects = React.useMemo(() => {
    return projects.filter(p => {
      if (statusFilter && p.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!p.project_name?.toLowerCase().includes(q) &&
          !p.project_code?.toLowerCase().includes(q) &&
          !p.client_name?.toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [projects, search, statusFilter]);

  const load = async () => {
    setLoading(true);
    try { const d = await api.listProjects(); setProjects(Array.isArray(d) ? d : []); } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (activeProjectId) {
    const dashboardProject = projects.find(p => p.project_id === activeProjectId);
    if (dashboardProject) {
      return (
        <AdminProjectDashboardView
          project={dashboardProject}
          onBack={() => setSearchParams({ tab: 'projects' })}
          toast={toast}
        />
      );
    }
  }

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = { ...form };
    if (!payload.currency) payload.currency = 'NGN';
    if (!payload.status) payload.status = 'planning';
    
    const errs = {};
    const req = ['project_title', 'project_name', 'project_code', 'currency', 'vat_rate', 'wht_rate', 'status'];
    req.forEach(k => {
      if (payload[k] === undefined || payload[k] === null || payload[k] === '') errs[k] = true;
    });
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.show('Required fields missing: ' + Object.keys(errs).join(', '), 'error');
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      if (modal === 'edit') await api.updateProject(payload.project_id, payload);
      else await api.createProject(payload);
      toast.show(modal === 'edit' ? 'Project updated' : 'Project created');
      setModal(null); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    setSaving(true);
    try { 
      await api.deleteProject(deleteConfirmId); 
      toast.show('Project deleted'); 
      load(); 
    }
    catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
    setDeleteConfirmId(null);
  };

  return (
    <div className="fade-in">
      <SectionHeader
        title="Admin Projects Management"
        action={access.add ? <Btn onClick={() => { setForm({ status: 'planning', currency: 'NGN', vat_rate: '7.5', wht_rate: '5' }); setModal('create'); }}>New Project</Btn> : null}
      />

      <Card style={{ padding: '20px 24px', marginBottom: 24, borderRadius: 12, border: '1px solid black' }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 250 }}>
            <Input label="Search Projects" placeholder="Search by name, code, or client..." value={search} onChange={v => setSearch(v)} />
          </div>
          <div style={{ width: 200 }}>
            <Select label="Status Filter" value={statusFilter} onChange={setStatusFilter} options={[{ value: '', label: 'All Statuses' }, ...STATUS_OPTS]} />
          </div>
          <div>
            <Btn variant="secondary" onClick={() => { setSearch(''); setStatusFilter(''); }} style={{ height: 42, background: '#cbccceff' }}>Clear Filters</Btn>
          </div>
        </div>
      </Card>

      {loading ? (
        <RNLLoader title="Loading Projects..." subtitle="Please wait" />
      ) : filteredProjects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No projects match your filters</div>
        </div>
      ) : (
        <Card>
          <Table cols={[
            { key: 'project_code', label: 'Code', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)' }}>{v}</span> },
            { key: 'project_title', label: 'Project Owner', render: v => <strong>{v || '—'}</strong> },
            { key: 'project_name', label: 'Project Name', render: v => <span>{v}</span> },
            { key: 'client_name', label: 'Client', render: v => <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{v || '—'}</span> },
            { key: 'status', label: 'Status', render: v => <Badge color={v === 'active' ? 'green' : v === 'planning' ? 'amber' : 'gray'}>{v.toUpperCase()}</Badge> },
            { key: 'contract_value', label: 'Contract Value', render: v => <span style={{ fontFamily: 'var(--mono)' }}>{fmt(v)}</span> },
            { key: 'start_date', label: 'Timeline', render: (v, row) => <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{row.start_date ? fmtDate(row.start_date) : 'TBA'} - {row.end_date ? fmtDate(row.end_date) : 'TBA'}</span> },
            {
              key: 'project_id', label: '', render: (id, p) => (
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn variant="ghost" size="sm" style={{ padding: '4px 10px' }} onClick={() => setSearchParams({ tab: 'projects', id: p.project_id })} disabled={openingProject === p.project_id}>
                    Configure
                  </Btn>
                  <Btn variant="ghost" size="sm" style={{ padding: '4px 10px' }} onClick={() => setViewProject(p)}>View</Btn>
                  {access.edit && <Btn variant="ghost" size="sm" style={{ padding: '4px 10px' }} onClick={() => { setForm({ ...p }); setModal('edit'); }}>Edit</Btn>}
                  {access.delete && <Btn variant="danger" size="sm" style={{ padding: '4px 10px' }} onClick={() => del(p.project_id)}>Del</Btn>}
                </div>
              )
            }
          ]} rows={filteredProjects} />
        </Card>
      )}

      {modal && (
        <Modal title={modal === 'edit' ? 'Edit Project' : 'Create Project'} onClose={() => setModal(null)} width={800} headerBg="#0b318bff" headerColor="#ffffff">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <FormGrid>
              <Input label="Project Owner" required value={form.project_title || ''} onChange={set('project_title')} error={errors.project_title} />
              <Input label="Project Name" required value={form.project_name || ''} onChange={set('project_name')} error={errors.project_name} />
              <Input label="Project Code" required value={form.project_code || ''} onChange={set('project_code')} error={errors.project_code} />
              <Input label="Client Name" value={form.client_name || ''} onChange={set('client_name')} error={errors.client_name} />
              <Input label="Location" value={form.location || ''} onChange={set('location')} error={errors.location} />
            </FormGrid>
            
            <FormGrid>
              <Input label="Contract Value" type="number" value={form.contract_value || ''} onChange={set('contract_value')} error={errors.contract_value} />
              <Input label="Original Budget" type="number" value={form.original_budget || ''} onChange={set('original_budget')} error={errors.original_budget} />
              <Input label="Contingency Budget" type="number" value={form.contingency_budget || ''} onChange={set('contingency_budget')} error={errors.contingency_budget} />
              <Select label="Currency" required value={form.currency || 'NGN'} onChange={set('currency')} options={['NGN', 'USD', 'EUR', 'GBP'].map(c => ({ value: c, label: c }))} error={errors.currency} />
            </FormGrid>

            <FormGrid>
              <Input label="VAT Rate (%)" type="number" required value={form.vat_rate || ''} onChange={set('vat_rate')} error={errors.vat_rate} />
              <Input label="WHT Rate (%)" type="number" required value={form.wht_rate || ''} onChange={set('wht_rate')} error={errors.wht_rate} />
              <Select label="Status" required value={form.status || ''} onChange={set('status')} options={STATUS_OPTS} error={errors.status} />
              <Select label="Contract Type" value={form.contract_type || ''} onChange={set('contract_type')} options={CONTRACT_OPTS} error={errors.contract_type} />
            </FormGrid>

            <FormGrid>
              <Input label="Start Date" type="date" value={form.start_date ? form.start_date.split('T')[0] : ''} onChange={v => set('start_date')(v ? v + 'T00:00:00Z' : '')} error={errors.start_date} />
              <Input label="End Date" type="date" value={form.end_date ? form.end_date.split('T')[0] : ''} onChange={v => set('end_date')(v ? v + 'T00:00:00Z' : '')} error={errors.end_date} />
            </FormGrid>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn variant="primary" onClick={save} loading={saving}>{modal === 'edit' ? 'Save Changes' : 'Create Project'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {viewProject && (
        <Modal title="Project Details" onClose={() => setViewProject(null)} width={600} headerBg="#0b318bff" headerColor="#ffffff">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px', fontSize: 14 }}>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>PROJECT OWNER</div> <div style={{ fontWeight: 600 }}>{viewProject.project_title || '—'}</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>PROJECT NAME</div> <div style={{ fontWeight: 600 }}>{viewProject.project_name}</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>PROJECT CODE</div> <div style={{ fontWeight: 600, fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{viewProject.project_code}</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>CLIENT NAME</div> <div style={{ fontWeight: 600 }}>{viewProject.client_name || '—'}</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>LOCATION</div> <div style={{ fontWeight: 600 }}>{viewProject.location || '—'}</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>STATUS</div> <div><Badge color={viewProject.status === 'active' ? 'green' : 'gray'}>{viewProject.status.toUpperCase()}</Badge></div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>CONTRACT TYPE</div> <div style={{ fontWeight: 600 }}>{viewProject.contract_type || '—'}</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>CURRENCY</div> <div style={{ fontWeight: 600 }}>{viewProject.currency}</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>CONTRACT VALUE</div> <div style={{ fontWeight: 600, fontFamily: 'var(--mono)' }}>{fmt(viewProject.contract_value)}</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>ORIGINAL BUDGET</div> <div style={{ fontWeight: 600, fontFamily: 'var(--mono)' }}>{fmt(viewProject.original_budget)}</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>VAT RATE</div> <div style={{ fontWeight: 600 }}>{viewProject.vat_rate}%</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>WHT RATE</div> <div style={{ fontWeight: 600 }}>{viewProject.wht_rate}%</div></div>
              <div><div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>TIMELINE</div> <div style={{ fontWeight: 600 }}>{viewProject.start_date ? fmtDate(viewProject.start_date) : '—'} to {viewProject.end_date ? fmtDate(viewProject.end_date) : '—'}</div></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <Btn variant="primary" onClick={() => setViewProject(null)}>Close</Btn>
            </div>
          </div>
        </Modal>
      )}

      {deleteConfirmId && (
        <Modal onClose={() => setDeleteConfirmId(null)} width={460} hideHeader>
          <div style={{ textAlign: 'center', padding: '10px 10px 0' }}>
            <div style={{
              width: 66, height: 66, background: '#fee2e2', borderRadius: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 26px', color: '#ef4444'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              </svg>
            </div>
            <h4 style={{ fontSize: 20, fontWeight: 800, marginBottom: 10, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>Delete Project</h4>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 30, padding: '0 8px' }}>
              Are you sure you want to delete this project? This action cannot be undone and all associated data will be lost.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Btn variant="ghost" onClick={() => setDeleteConfirmId(null)} size="md" style={{ padding: '11px 0', fontSize: 14, fontWeight: 700 }}>Cancel</Btn>
            <Btn variant="primary" onClick={confirmDelete} loading={saving} size="md" style={{ padding: '11px 0', fontSize: 14, fontWeight: 700, background: '#ef4444', color: '#fff', border: 'none', boxShadow: '0 8px 20px -6px #ef4444' }}>Delete Project</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
